import React, { useState, useEffect } from 'react';
import { Zap, Crown, Clock, Trophy, Lock, ArrowRight, Star } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';

import { BACKEND_URL } from '../config/api';

/**
 * QuickResponderBadge Component
 * 
 * Displays the "Quick Responder" badge for fast-responding Pro users.
 * Compact badge suitable for profile cards and search results.
 */
export function QuickResponderBadge({ userId, size = 'sm', showTooltip = true }) {
  const [hasBadge, setHasBadge] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      checkBadge();
    }
  }, [userId]);

  const checkBadge = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/response-time/badge/${userId}`
      );
      setHasBadge(response.data.has_badge);
    } catch (error) {
      console.warn('ResponseBadge: failed to fetch badge', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !hasBadge) {
    return null;
  }

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1'
  };

  return (
    <span 
      className={`inline-flex items-center gap-1 bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-700 font-medium rounded-full border border-amber-200 ${sizeClasses[size]}`}
      title={showTooltip ? "Responds within 5 minutes" : undefined}
      data-testid="quick-responder-badge"
    >
      <Zap className={size === 'xs' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      Quick Responder
    </span>
  );
}

/**
 * ResponseTimeLeaderboard Component
 * 
 * Simple leaderboard showing top 5 fastest responders.
 * Shows upgrade prompt for non-Pro users.
 */
export function ResponseTimeLeaderboard({ 
  location = null, 
  trade = null, 
  onUpgrade,
  compact = false 
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [location, trade]);

  const fetchLeaderboard = async () => {
    try {
      const params = new URLSearchParams();
      if (location) params.append('location', location);
      if (trade) params.append('trade', trade);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/response-time/leaderboard?${params.toString()}`,
        { withCredentials: true }
      );
      setData(response.data);
    } catch (error) {
      // Try public endpoint if auth fails
      try {
        const params = new URLSearchParams();
        if (location) params.append('location', location);
        if (trade) params.append('trade', trade);
        
        const response = await axios.get(
          `${BACKEND_URL}/api/response-time/leaderboard/public?${params.toString()}`
        );
        setData(response.data);
      } catch (error) {
        console.warn('Leaderboard: failed to fetch data', error?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-slate-200 ${compact ? 'p-3' : 'p-4'} animate-pulse`}>
        <div className="h-5 bg-slate-200 rounded w-2/3 mb-3"></div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-slate-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || !data.enabled) {
    return null;
  }

  const { leaderboard, show_upgrade_prompt } = data;

  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 ${compact ? 'p-3' : 'p-4'}`}
      data-testid="response-leaderboard"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-4 w-4 text-amber-500" />
        <h3 className={`font-semibold text-slate-900 ${compact ? 'text-sm' : 'text-base'}`}>
          Fastest Responders{location ? ` Near ${location}` : ''}
        </h3>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 ? (
        <div className="space-y-2">
          {leaderboard.map((user, index) => (
            <div 
              key={user.user_id || `user-${index}`}
              className={`flex items-center gap-3 ${compact ? 'py-1.5' : 'py-2'} ${
                index < leaderboard.length - 1 ? 'border-b border-slate-100' : ''
              }`}
            >
              {/* Rank */}
              <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                index === 0 ? 'bg-amber-100 text-amber-700' :
                index === 1 ? 'bg-slate-200 text-slate-600' :
                index === 2 ? 'bg-orange-100 text-orange-700' :
                'bg-slate-100 text-slate-500'
              }`}>
                {user.rank}
              </div>

              {/* User info */}
              <div className="flex-1 min-w-0">
                <p className={`font-medium text-slate-900 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                  {user.display_name}
                </p>
                {!compact && user.trade && (
                  <p className="text-xs text-slate-500 truncate">{user.trade}</p>
                )}
              </div>

              {/* Badge + Response time */}
              <div className="flex items-center gap-2">
                {user.has_quick_responder_badge && (
                  <Zap className="h-3.5 w-3.5 text-amber-500" />
                )}
                <span className={`text-emerald-600 font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
                  {user.avg_response_time < 1 
                    ? '<1 min' 
                    : `${Math.round(user.avg_response_time)} min`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 text-center py-4">
          No responders yet in this area
        </p>
      )}

      {/* Priority Boost prompt */}
      {show_upgrade_prompt && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
            <Lock className="h-3 w-3" />
            <span>Top responders hold an active Priority Boost</span>
          </div>
          {onUpgrade && (
            <Button
              size="sm"
              onClick={onUpgrade}
              className="w-full bg-orange-500 hover:bg-orange-600 text-xs"
              data-testid="leaderboard-upgrade-btn"
            >
              <Crown className="h-3 w-3 mr-1" />
              Earn Priority Boost
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * MyResponseStats Component
 * 
 * Shows the current user's response time stats and badge status.
 * For tradesperson dashboard.
 */
export function MyResponseStats({ onUpgrade }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/response-time/my-stats`,
        { withCredentials: true }
      );
      setData(response.data);
    } catch (error) {
      console.warn('ResponseStats: failed to fetch stats', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-5 bg-slate-200 rounded w-1/2 mb-3"></div>
        <div className="h-16 bg-slate-100 rounded"></div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { stats, badge, rank, has_pro, threshold_minutes } = data;

  // Non-Pro users see upgrade prompt
  if (!has_pro) {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-4" data-testid="response-stats-upgrade">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="h-4 w-4 text-amber-500" />
          <h3 className="font-semibold text-amber-900 text-sm">Response Leaderboard</h3>
        </div>
        <p className="text-xs text-amber-700 mb-3">
          Refer other trades to earn a Priority Boost and compete for fastest response badges.
        </p>
        {onUpgrade && (
          <Button
            size="sm"
            onClick={onUpgrade}
            className="w-full bg-orange-500 hover:bg-orange-600 text-xs"
          >
            <Zap className="h-3 w-3 mr-1" />
            Earn Priority Boost
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="my-response-stats">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Your Response Time</h3>
        </div>
        {badge.has_badge && (
          <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
            <Zap className="h-3 w-3" />
            Quick Responder
          </span>
        )}
      </div>

      {/* Stats */}
      {stats.total_responses > 0 ? (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-slate-900">
              {stats.avg_response_time ? `${Math.round(stats.avg_response_time)}m` : '-'}
            </p>
            <p className="text-[10px] text-slate-500">Avg Time</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-slate-900">{stats.total_responses}</p>
            <p className="text-[10px] text-slate-500">Responses</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-slate-900">#{rank || '-'}</p>
            <p className="text-[10px] text-slate-500">Rank</p>
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 rounded-lg p-3 text-center mb-3">
          <p className="text-sm text-slate-600">Respond to urgent jobs to start tracking!</p>
        </div>
      )}

      {/* Badge progress */}
      {!badge.has_badge && stats.total_responses > 0 && (
        <div className="bg-amber-50 rounded-lg p-2 text-xs text-amber-700">
          <p className="font-medium flex items-center gap-1">
            <Star className="h-3 w-3" />
            {badge.eligibility.needed} more quick responses needed for badge
          </p>
          <p className="text-amber-600 mt-0.5">
            Respond within {threshold_minutes} minutes to qualify
          </p>
        </div>
      )}
    </div>
  );
}

export default {
  QuickResponderBadge,
  ResponseTimeLeaderboard,
  MyResponseStats
};
